import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Input } from '@saas-kit/ui';

export interface AuditFilterValues {
  tenant_id: string;
  actor_id: string;
  action: string;
}

interface Props {
  values: AuditFilterValues;
  onApply: (next: AuditFilterValues) => void;
  onClear: () => void;
}

export function AuditFilters({ values, onApply, onClear }: Props): JSX.Element {
  const { t } = useTranslation('adminAudit');
  const [draft, setDraft] = useState<AuditFilterValues>(values);

  useEffect(() => {
    setDraft(values);
  }, [values]);

  function update<K extends keyof AuditFilterValues>(
    key: K,
    next: AuditFilterValues[K],
  ): void {
    setDraft((prev) => ({ ...prev, [key]: next }));
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    onApply({
      tenant_id: draft.tenant_id.trim(),
      actor_id: draft.actor_id.trim(),
      action: draft.action.trim(),
    });
  }

  function handleClear(): void {
    setDraft({ tenant_id: '', actor_id: '', action: '' });
    onClear();
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="grid gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-3"
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t('filters.tenant_id_label')}</span>
        <Input
          value={draft.tenant_id}
          onChange={(e) => update('tenant_id', e.target.value)}
          placeholder={t('filters.tenant_id_placeholder')}
          autoComplete="off"
          spellCheck={false}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t('filters.actor_id_label')}</span>
        <Input
          value={draft.actor_id}
          onChange={(e) => update('actor_id', e.target.value)}
          placeholder={t('filters.actor_id_placeholder')}
          autoComplete="off"
          spellCheck={false}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t('filters.action_label')}</span>
        <Input
          value={draft.action}
          onChange={(e) => update('action', e.target.value)}
          placeholder={t('filters.action_placeholder')}
          autoComplete="off"
          spellCheck={false}
        />
      </label>
      <div className="flex items-end gap-2 sm:col-span-3 sm:justify-end">
        <Button type="button" variant="ghost" onClick={handleClear}>
          {t('filters.clear')}
        </Button>
        <Button type="submit">{t('filters.apply')}</Button>
      </div>
    </form>
  );
}
